import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Pause, Play, Square, Loader2, Wifi, WifiOff } from 'lucide-react';

type RecorderState = 'idle' | 'recording' | 'paused' | 'processing' | 'done' | 'error';

interface AudioRecorderProps {
  onAudioReady: (blob: Blob) => void;
  disabled?: boolean;
}

// IndexedDB helpers for offline buffering
const DB_NAME = 'ikhangynae-audio';
const STORE_NAME = 'chunks';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function storeChunk(chunk: Blob): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).add({ data: chunk, timestamp: Date.now() });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getAllChunks(): Promise<Blob[]> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result.map((r: { data: Blob }) => r.data));
    req.onerror = () => reject(req.error);
  });
}

async function clearChunks(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function AudioRecorder({ onAudioReady, disabled }: AudioRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [duration, setDuration] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Online/offline tracking
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Auto-upload buffered audio when back online
  useEffect(() => {
    if (isOnline && state === 'done') {
      getAllChunks().then(chunks => {
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          onAudioReady(blob);
          clearChunks();
        }
      });
    }
  }, [isOnline, state, onAudioReady]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer mp4 (AAC) — Gemini transcribes it more reliably than webm/opus
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      chunksRef.current = [];
      await clearChunks();

      mediaRecorder.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          // Buffer to IndexedDB for offline resilience
          await storeChunk(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const allChunks = chunksRef.current.length > 0
          ? chunksRef.current
          : await getAllChunks();

        if (allChunks.length > 0) {
          const blob = new Blob(allChunks, { type: mimeType });
          onAudioReady(blob);
          await clearChunks();
        }

        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      // Request data every 5 seconds for incremental IndexedDB writes
      mediaRecorder.start(5000);
      mediaRecorderRef.current = mediaRecorder;
      setState('recording');
      setDuration(0);

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
      setState('error');
    }
  }, [onAudioReady]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setState('paused');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setState('recording');
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setState('processing');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setDuration(0);
    chunksRef.current = [];
    clearChunks();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  return (
    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
      {/* Status indicator */}
      <div className="flex items-center gap-2">
        {!isOnline && (
          <span className="flex items-center gap-1 text-xs text-amber-600" title="Offline — audio buffered locally">
            <WifiOff className="h-3.5 w-3.5" />
          </span>
        )}
        {isOnline && state === 'recording' && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <Wifi className="h-3.5 w-3.5" />
          </span>
        )}
      </div>

      {/* Recording indicator */}
      {state === 'recording' && (
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm font-mono text-gray-700">{formatDuration(duration)}</span>
        </span>
      )}
      {state === 'paused' && (
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
          <span className="text-sm font-mono text-gray-500">{formatDuration(duration)} (paused)</span>
        </span>
      )}
      {state === 'processing' && (
        <span className="flex items-center gap-1.5 text-sm text-blue-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Processing...
        </span>
      )}

      {/* Controls */}
      <div className="flex items-center gap-2 ml-auto">
        {state === 'idle' && (
          <button
            onClick={startRecording}
            disabled={disabled}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <Mic className="h-4 w-4" /> Start Recording
          </button>
        )}

        {state === 'recording' && (
          <>
            <button onClick={pauseRecording} className="p-2 text-gray-600 hover:text-gray-800 transition-colors" title="Pause">
              <Pause className="h-5 w-5" />
            </button>
            <button onClick={stopRecording} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-md hover:bg-gray-800 transition-colors">
              <Square className="h-4 w-4" /> Stop
            </button>
          </>
        )}

        {state === 'paused' && (
          <>
            <button onClick={resumeRecording} className="p-2 text-green-600 hover:text-green-800 transition-colors" title="Resume">
              <Play className="h-5 w-5" />
            </button>
            <button onClick={stopRecording} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-md hover:bg-gray-800 transition-colors">
              <Square className="h-4 w-4" /> Stop
            </button>
          </>
        )}

        {state === 'error' && (
          <div className="flex items-center gap-2">
            <MicOff className="h-4 w-4 text-red-500" />
            <span className="text-sm text-red-600">Microphone access denied</span>
            <button onClick={reset} className="text-sm text-blue-600 hover:underline">Retry</button>
          </div>
        )}

        {(state === 'done' || state === 'processing') && (
          <button onClick={reset} className="text-sm text-gray-500 hover:text-gray-700">
            Record again
          </button>
        )}
      </div>
    </div>
  );
}
